import React, { useState } from "react";
import { BankIcon } from "@icons";
import { OptionGroupItem } from "@design-system/OptionGroup/OptionGroupItem";
import { SelectableItem } from "@design-system/SelectableItem/SelectableItem";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { BooleanControl } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./DatePickerPlayground.module.scss";

const accounts = [
    { value: "checking", title: "Checking", description: "Operating account" },
    { value: "savings", title: "Savings", description: "Reserve account" },
    {
        value: "credit-card",
        title: "Credit card",
        description: "Card spending",
    },
];

const selectionModes = ["single", "multiple"] as const;

type SelectionMode = (typeof selectionModes)[number];

const iconSizeOptions = ["50", "60", "75", "100"] as const;

type IconSizeOption = (typeof iconSizeOptions)[number];

const iconSizeByOption: Record<IconSizeOption, 50 | 60 | 75 | 100> = {
    "50": 50,
    "60": 60,
    "75": 75,
    "100": 100,
};

const toggleInSet = (values: string[], value: string) =>
    values.includes(value)
        ? values.filter((selectedValue) => selectedValue !== value)
        : [...values, value];

const applySelection = ({
    values,
    value,
    mode,
}: {
    values: string[];
    value: string;
    mode: SelectionMode;
}) => (mode === "single" ? [value] : toggleInSet(values, value));

export const SelectableCardsPlayground = () => {
    const [optionGroupValues, setOptionGroupValues] = useState<string[]>([
        "savings",
    ]);
    const [selectableValues, setSelectableValues] = useState<string[]>([
        "checking",
    ]);
    const [isDisabled, setIsDisabled] = useState(false);
    const [hasIcon, setHasIcon] = useState(true);
    const [iconSizeOption, setIconSizeOption] = useState<IconSizeOption>("50");
    const [selectionMode, setSelectionMode] = useState<SelectionMode>("single");
    const [hasInstantSelect, setHasInstantSelect] = useState(false);
    const [hasDescription, setHasDescription] = useState(true);
    const [hasFooter, setHasFooter] = useState(false);

    const isMultiple = selectionMode === "multiple";
    const icon = hasIcon ? <BankIcon /> : undefined;

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Both components</h2>
                        <div className={styles.booleanGrid}>
                            <BooleanControl
                                label="Disabled"
                                checked={isDisabled}
                                onChange={setIsDisabled}
                            />
                            <BooleanControl
                                label="Icon"
                                checked={hasIcon}
                                onChange={setHasIcon}
                            />
                        </div>
                    </section>
                    <section>
                        <h2>SelectableItem selection</h2>
                        <ToggleGroup.Root<SelectionMode>
                            value={selectionMode}
                            onValueChange={(nextMode) => {
                                if (nextMode) {
                                    setSelectionMode(nextMode);
                                    if (nextMode === "single") {
                                        setSelectableValues((values) =>
                                            values.slice(0, 1),
                                        );
                                    }
                                }
                            }}
                        >
                            {selectionModes.map((option) => (
                                <ToggleGroup.Item key={option} value={option}>
                                    {option}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                        <div className={styles.booleanGrid}>
                            <BooleanControl
                                label="Instant select (acts as a button)"
                                checked={hasInstantSelect}
                                onChange={setHasInstantSelect}
                            />
                        </div>
                    </section>
                    <section>
                        <h2>SelectableItem content</h2>
                        <div className={styles.booleanGrid}>
                            <BooleanControl
                                label="Description"
                                checked={hasDescription}
                                onChange={setHasDescription}
                            />
                            <BooleanControl
                                label="Footer"
                                checked={hasFooter}
                                onChange={setHasFooter}
                            />
                        </div>
                        <h2 className={styles.subheading}>Icon size</h2>
                        <ToggleGroup.Root<IconSizeOption>
                            value={iconSizeOption}
                            onValueChange={(nextIconSize) => {
                                if (nextIconSize) {
                                    setIconSizeOption(nextIconSize);
                                }
                            }}
                        >
                            {iconSizeOptions.map((option) => (
                                <ToggleGroup.Item key={option} value={option}>
                                    {option}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary="OptionGroupItem · SelectableItem"
                output={JSON.stringify(
                    {
                        OptionGroupItem: {
                            disabled: isDisabled,
                            icon: hasIcon,
                        },
                        SelectableItem: {
                            disabled: isDisabled,
                            icon: hasIcon,
                            iconSize: iconSizeByOption[iconSizeOption],
                            description: hasDescription,
                            footer: hasFooter,
                            multiple: isMultiple,
                            instantSelect: hasInstantSelect,
                        },
                        showcaseState: {
                            optionGroupSelection: optionGroupValues,
                            selectableItemSelection: selectableValues,
                            selectionMode,
                        },
                    },
                    null,
                    2,
                )}
            >
                <section className={styles.surface}>
                    <div className={styles.panelContent}>
                        <h3>OptionGroupItem</h3>
                        {accounts.map((account) => (
                            <OptionGroupItem
                                key={account.value}
                                title={account.title}
                                icon={icon}
                                selected={optionGroupValues.includes(
                                    account.value,
                                )}
                                disabled={isDisabled}
                                onToggle={() =>
                                    setOptionGroupValues((values) =>
                                        toggleInSet(values, account.value),
                                    )
                                }
                            />
                        ))}
                    </div>
                </section>
                <section className={styles.surface}>
                    <div className={styles.panelContent}>
                        <h3>SelectableItem</h3>
                        {accounts.map((account) => (
                            <SelectableItem
                                key={account.value}
                                title={account.title}
                                description={
                                    hasDescription
                                        ? account.description
                                        : undefined
                                }
                                icon={icon}
                                iconSize={iconSizeByOption[iconSizeOption]}
                                footer={
                                    hasFooter
                                        ? "Synced 2 minutes ago"
                                        : undefined
                                }
                                selected={selectableValues.includes(
                                    account.value,
                                )}
                                disabled={isDisabled}
                                multiple={isMultiple}
                                instantSelect={hasInstantSelect}
                                onSelect={() =>
                                    setSelectableValues((values) =>
                                        applySelection({
                                            values,
                                            value: account.value,
                                            mode: selectionMode,
                                        }),
                                    )
                                }
                            />
                        ))}
                    </div>
                </section>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
